import { kqlEscape } from '../escape-utils'

import { Tool } from './base'
import {
  InlineGroups,
  makeRenderer,
  normalizeList,
  RenderContext,
  RenderOut,
  scalarToString,
} from './render/render-kit'
import { NormalizedDataSource } from './render/rtd/datasource.types'
import { joinWithAnd, wrapRendererWithDatasource } from './render/rtd/datasource.utils'
import { s1SingularityMap } from './render/rtd/tool-datasource-mappings/sentinelone-singularity.datasource'
import { AstPredicate } from './render/types'

export { s1SingularityMap }

export class SentinelOneSingularityTool extends Tool {
  public readonly toolName = 'SentinelOne Singularity'
  public readonly toolKind = 'xdr' as const

  public generateSearchPermalink(
    rootURL: string,
    renderedContent: string,
  ): string {
    const encodedRenderedContent = encodeURIComponent(renderedContent)
    return `${rootURL}/events?view=xdr&filter=${encodedRenderedContent}&startTime=24+hours`
  }

  public pivotEscape(key: string, value: string): string {
    return kqlEscape(value)
  }

  /** Notes and Implementation
   * Fields
   * - Map via toolTuningFields["sentinelone_singularity"]; **no Eta** wrapping.
   *
   * Values & Lists
   * - Always **single-quoted**; escape \ and '.
   * - Multi-values: `('v1','v2',...)`.
   *
   * Logic & Grouping
   * - Uses InlineGroups: **no root parentheses**; nested groups parenthesized.
   * - Glue tokens: ` and ` / ` or ` (lowercase by config).
   * - Negation is a **logical wrapper**: `!( <clause> )`.
   *
   * Operators
   * - eq: single `F = 'v'`; multi `F in:anycase ('v1','v2')`.
   * - neq: single `!(F = 'v')`; multi `!(F in:anycase ('v1','v2'))`.
   * - contains / doesnotcontain: `F contains:anycase 'v'` / `!(F contains:anycase 'v')` (lists supported).
   * - startswith / endswith: `F matches:anycase '^value'` / `F matches:anycase 'value$'` (lists supported).
   * - Comparisons: `F < 'v'`, `<=`, `>`, `>=` (quoted).
   *
   * Null / Empty (confirmed)
   * - isnull → `!(F = *)`; isnotnull → `F = *`; isempty → `!(F = *)`; isnotempty → `F = *`.
   *
   * Safety
   * - Sanitize identifiers; use centralized single-quote `quote()`/`list()` helpers.
   */

  public renderTuning = makeRenderer({
    tool: this.toolName,
    groupStrategy: InlineGroups,
    renderContext: {
      // Single-quoted values with escaping for \ and '
      quote: (s: string) =>
        `'${String(s).replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`,
      // ('value1','value2','value3') using the same escaping
      list: (vals: string[]) =>
        `('${vals
          .map((v) => String(v).replace(/\\/g, '\\\\').replace(/'/g, "\\'"))
          .join("','")}')`,
      andGlue: ' and ',
      orGlue: ' or ',
      // No Eta wrapper for this tool:
      wrapField: (f) => f,
    },
    formatPredicate: (
      p: AstPredicate,
      renderContext: RenderContext,
    ): string => {
      const F = renderContext.wrapField(renderContext.mapField(p.field))
      const vals = normalizeList(p.value).map(scalarToString)

      // format "wildcarding" per value as needed
      const shaped = vals.map((s) => {
        switch (p.op) {
          case 'startswith':
            return `^${s}` // left-side anchor
          case 'endswith':
            return `${s}$` // right-side anchor
          // contains/doesnotcontain: no wildcarding needed
          default:
            return s // eq/neq and others unchanged
        }
      })
      const isMulti = shaped.length > 1

      const Q = (s: string) => renderContext.quote(s)
      const LIST = () => renderContext.list(shaped)

      // helpers for negation style: wrap final clause in !( ... )
      const NOT = (clause: string) => `!(${clause})`

      switch (p.op) {
        // equality / inequality
        case 'eq':
          return isMulti
            ? `${F} in:anycase ${LIST()}`
            : `${F} = ${Q(shaped[0])}`
        case 'neq':
          return isMulti
            ? NOT(`${F} in:anycase ${LIST()}`)
            : NOT(`${F} = ${Q(shaped[0])}`)

        // contains:anycase family (supports single or list)
        case 'contains':
          return isMulti
            ? `${F} contains:anycase ${LIST()}`
            : `${F} contains:anycase ${Q(shaped[0])}`
        case 'doesnotcontain':
          return isMulti
            ? NOT(`${F} contains:anycase ${LIST()}`)
            : NOT(`${F} contains:anycase ${Q(shaped[0])}`)

        // startswith/endswith are also expressed via contains:anycase + wildcard
        case 'startswith':
          return isMulti
            ? `${F} matches:anycase ${LIST()}`
            : `${F} matches:anycase ${Q(shaped[0])}`
        case 'endswith':
          return isMulti
            ? `${F} matches:anycase ${LIST()}`
            : `${F} matches:anycase ${Q(shaped[0])}`

        // comparisons: keep quoted per your global rule
        case 'lt':
          return `${F} < ${Q(shaped[0])}`
        case 'lte':
          return `${F} <= ${Q(shaped[0])}`
        case 'gt':
          return `${F} > ${Q(shaped[0])}`
        case 'gte':
          return `${F} >= ${Q(shaped[0])}`

        // empties / nulls (best-effort; adjust if you need specific S1 functions)
        case 'isnull':
          return NOT(`${F} = *`)
        case 'isnotnull':
          return `${F} = *`
        case 'isempty':
          return NOT(`${F} = *`)
        case 'isnotempty':
          return `${F} = *`

        default:
          // sane default = equality
          return isMulti ? `${F} = ${LIST()}` : `${F} = ${Q(shaped[0])}`
      }
    },
    finalize: (out: RenderOut, renderContext: RenderContext): string => {
      const base =
        out.kind === 'text' ? out.text : out.lines.join(renderContext.andGlue)
      if (!base) return ''
      return `(${base})`
    },
  })

  public readonly tuningText =
    'Append tuning logic to the end of the hunt logic. In some cases the hunt logic will need to be encapsulated in parenthesis, when tuning logic does not work as desired.'

  public override readonly datasourceMap = s1SingularityMap

  /**
   * Emit the Singularity prelude — the complete event.category/event.type clause
   * stored in events[0] (e.g. `event.category = 'process' AND event.type = 'Process Creation'`).
   */
  public emitS1SingularityDatasource(ds: NormalizedDataSource | undefined): string {
    return ds?.events?.[0] ?? ''
  }

  /** Emit prelude AND tuning body (no finalize hook — uses joinWithAnd). */
  public readonly renderSentinelOneSingularity = wrapRendererWithDatasource(
    this.renderTuning,
    this.toolName,
    (ds) => this.emitS1SingularityDatasource(ds),
    joinWithAnd,
    this.tuningText,
  )
}
